import { Ref, useCallback, useState } from 'react';

import Analytics, { ElementShownAnchor } from '@hh.ru/analytics-js';
import employerVacanciesResponsesVerificationReportInfotipCloseClick from '@hh.ru/analytics-js-events/build/xhh/employer/experiments/PORTFOLIO-32152/employer_vacancies_responses_verification_report_infotip_close_click';
import employerVacanciesResponsesVerificationReportInfotipElementShown from '@hh.ru/analytics-js-events/build/xhh/employer/experiments/PORTFOLIO-32152/employer_vacancies_responses_verification_report_infotip_element_shown';
import employerVacanciesResponsesVerificationReportInfotipOpenClick from '@hh.ru/analytics-js-events/build/xhh/employer/experiments/PORTFOLIO-32152/employer_vacancies_responses_verification_report_infotip_open_click';
import { Tag, Button } from '@hh.ru/magritte-ui';
import { CircleFilledSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Notices from 'Modules/Notices';
import { updateUrl } from 'Modules/url';
import MagritteInfotip from 'src/components/Infotip/MagritteInfotip';
import { makeResumeHref } from 'src/components/ResumeCard/utils/makeResumeHref';
import translation from 'src/components/translation';
import { useSelectorNonNullable, useSelector } from 'src/hooks/useSelector';
import { VerifiedSkillsWithReportsFlag } from 'src/models/resume/resumeVerifiedSkills.types';
import type { ResumeCardData } from 'src/models/resumeCard';

const TrlKeys = {
    text: 'label.skillsVerified',
    infotipTitle: 'skill.verification.infotip.title',
    infotipDesc: 'skill.verification.infotip.desc',
    infotipOpen: 'skill.verification.infotip.open',
    infotipClose: 'skill.verification.infotip.close',
};

const VERIFICATION_REPORTS_RESPONSE_INFOTIP = 'verification_reports_response_infotip';

export interface SkillsVerifiedProps {
    skillsVerified: VerifiedSkillsWithReportsFlag;
    resume: ResumeCardData;
    isResponse: boolean;
}

const SkillsVerified: TranslatedComponent<SkillsVerifiedProps> = ({ skillsVerified, resume, isResponse, trls }) => {
    const vacancyId = useSelector((state) => state.vacanciesShort.vacanciesList[0]?.vacancyId);
    const employerId = useSelectorNonNullable((state) => state.employerId);
    const showInfotip = useSelector(
        ({ infoTip }) =>
            infoTip?.name === VERIFICATION_REPORTS_RESPONSE_INFOTIP && skillsVerified.hasReports && !!vacancyId
    );

    const [isShowInfo, setShowInfo] = useState(showInfotip);

    const onClose = useCallback(() => {
        setShowInfo(false);
        if (vacancyId) {
            employerVacanciesResponsesVerificationReportInfotipCloseClick({
                vacancyId: vacancyId.toString(),
                employerId,
            });
        }
    }, [employerId, vacancyId]);

    const onOpen = useCallback(() => {
        Notices.markAsViewed(VERIFICATION_REPORTS_RESPONSE_INFOTIP);
        setShowInfo(false);
        if (vacancyId) {
            employerVacanciesResponsesVerificationReportInfotipOpenClick({
                vacancyId: vacancyId.toString(),
                employerId,
            });
            const resumeHref = makeResumeHref({
                id: resume._attributes.id,
                hash: resume._attributes.hash,
                simhash: resume._attributes.sim_hash,
                topicId: resume.topicId,
                highlight: '',
                isResponse,
                vacancyId,
            });
            const url = updateUrl(resumeHref, { showReports: true });
            window.open(url, '_blank', 'noopener');
        }
    }, [
        employerId,
        isResponse,
        resume._attributes.hash,
        resume._attributes.id,
        resume._attributes.sim_hash,
        resume.topicId,
        vacancyId,
    ]);

    return (
        <MagritteInfotip
            onClose={onClose}
            visible={isShowInfo}
            placement={'right-center'}
            title={trls[TrlKeys.infotipTitle]}
            name={VERIFICATION_REPORTS_RESPONSE_INFOTIP}
            render={({ activatorRef }) => (
                <div ref={activatorRef as Ref<HTMLDivElement>}>
                    <ElementShownAnchor
                        fn={Analytics.sendHHEventElementShown}
                        name="verification_key_skill_info"
                        keySkill={skillsVerified.skills.map(({ name }) => name).join(',')}
                        resumeId={resume._attributes.id}
                    >
                        <Tag
                            style="positive"
                            icon={CircleFilledSize16}
                            data-qa="resume-card-tag resume-card-tag_skills-verified"
                        >
                            {trls[TrlKeys.text]}
                        </Tag>
                    </ElementShownAnchor>
                </div>
            )}
            buttons={(onClose) => (
                <>
                    <Button onClick={onOpen} style="contrast" mode="primary" size="small">
                        {trls[TrlKeys.infotipOpen]}
                    </Button>
                    <Button style="contrast" mode="secondary" size="small" onClick={onClose}>
                        {trls[TrlKeys.infotipClose]}
                    </Button>
                </>
            )}
        >
            <ElementShownAnchor
                fn={employerVacanciesResponsesVerificationReportInfotipElementShown}
                vacancyId={vacancyId?.toString() || ''}
                employerId={employerId}
            >
                <div style={{ width: '400px' }}>{trls[TrlKeys.infotipDesc]}</div>
            </ElementShownAnchor>
        </MagritteInfotip>
    );
};

export default translation(SkillsVerified);
